import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import UnclaimedDraftEmbeddedSigning from "components/lib/Shared/ESignature/UnclaimedDraftEmbeddedSigning";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { convertOfficialDetailsToSignerDetails } from "utils/esignature";

// List of file names for e-signature documents
const FILE_NAMES = ["directorApprovalPdf"];

// Helper to construct the file URL for a given file name
const getFileUrl = (fileName, eSignatureFiles) =>
  `${process.env.REACT_APP_IMAGE_BASE_URL}/images/${eSignatureFiles[fileName]}`;

// Helper to get signer details for a given designation
const getSigners = (companyOfficials, designation) =>
  companyOfficials
    .filter(
      (official) =>
        official.designation === designation ||
        official.designation === "Director Shareholder"
    )
    .map(convertOfficialDetailsToSignerDetails);

/**
 * ESignature component for handling e-signature flows for directors and shareholders.
 * Shows a signing UI or a loading spinner depending on data readiness.
 *
 * @param {function} handleConfirmDetails - Callback to confirm details after signing
 * @param {object} selectedTab - The currently selected tab (director/shareholder approval)
 */
const DirectorsESignature = ({ handleConfirmDetails }) => {
  // State for current signers and file URLs
  const [signers, setSigners] = useState([]);
  const [fileUrls, setFileUrls] = useState([]);

  // Redux selectors for required data
  const transactionUnderReview = useSelector(
    (state) => state.transactionSlice.transactionUnderReview
  );
  const eSignatureFiles = transactionUnderReview.items?.eSignatureFiles || {};
  const currentUser = useSelector((state) => state.userSlice);
  const companyOfficials = useSelector(
    (state) => state.companyOfficialsSlice.companyOfficials.items
  );

  // Metadata for the signing process
  const metadata = {
    transactionId: transactionUnderReview?.items?.transactions?.[0]?._id,
    fileNames: FILE_NAMES,
    user: { _id: currentUser._id },
  };

  // Update signers and file URLs when the selected tab or data changes
  useEffect(() => {
    setSigners(getSigners(companyOfficials, "Director"));
    setFileUrls([getFileUrl("directorApprovalPdf", eSignatureFiles)]);
  }, [companyOfficials, eSignatureFiles]);

  // Render the signing UI if data is ready, otherwise show a loading spinner
  if (signers.length > 0 && fileUrls.length > 0) {
    return (
      <div>
        <h2 className="text-xl font-semibold mb-4">Director Approval Documents</h2>
        <UnclaimedDraftEmbeddedSigning
          signers={signers}
          fileUrls={fileUrls}
          metadata={metadata}
          functionsToCall={[handleConfirmDetails]}
        />
      </div>
    );
  }

  return (
    <div>
      <h1>
        <SpinnerIcon /> Getting details ...
      </h1>
    </div>
  );
};

export default DirectorsESignature;
